import { Button } from '@/components/Buttons';
import { ARIA_LABELS, BUTTON_LABELS, CARD_LABELS } from '@/constants/labels';
import { cn } from '@/utils';
import { ProgressBar } from '@/components/ProgressBar';
import { ScoreProcessingCardProps } from './ScoreProcessingCard.types';
import { Spinner } from '@/components/Loaders';
import React, { useMemo } from 'react';

/**
 * ScoreProcessingCard is a component that displays a loading state while a
 * user's score is being processed. It includes a progress bar, a spinner, and
 * action buttons to navigate to the dashboard or retake the roleplay. The
 * component is designed to be flexible, allowing for customization of labels,
 * progress value, and visibility of elements.
 *
 * @todo Integrate with future API to fetch real-time progress updates and handle completion state.
 */
export const ScoreProcessingCard: React.FC<ScoreProcessingCardProps> = ({
  className,
  description = CARD_LABELS.SCORE_PROCESSING_DESCRIPTION,
  onGoToDashboard,
  onRetakeRoleplay,
  primaryButtonLabel = BUTTON_LABELS.GO_TO_DASHBOARD,
  progress = 75,
  secondaryButtonLabel = BUTTON_LABELS.TRY_AGAIN,
  showButtons = true,
  showProgress = true,
  title = CARD_LABELS.SCORE_PROCESSING_TITLE,
  variant = 'default',
  ...props
}) => {
  // =======================================
  // Memoized Values
  // =======================================

  /**
   * Clamped progress value between 0 and 100
   */
  const clampedProgress = useMemo(() => Math.min(Math.max(progress, 0), 100), [progress]);

  // =======================================
  // Subcomponents
  // =======================================

  /**
   * Renders the title of the card
   */
  const Title = ({ title }: { title: string }) => {
    if (!title) return null;
    return <h3 className="typography-section-subtitle text-neutral-black">{title}</h3>;
  };

  /**
   * Renders the description text of the card
   */
  const Description = ({ description }: { description: string }) => {
    if (!description) return null;
    return <p className="typography-body text-neutral-gray-800">{description}</p>;
  };

  /**
   * Renders the progress bar if showProgress is true
   */
  const Progress = ({ progress }: { progress: number }) => {
    if (!showProgress) return null;
    return (
      <div className="mb-0">
        <ProgressBar
          ariaLabel={ARIA_LABELS.SCORE_PROCESSING_PROGRESS.replace(
            '%PROGRESS_VALUE%',
            progress.toString(),
          )}
          className="w-full"
          size="medium"
          value={progress}
          variant="primary"
        />
      </div>
    );
  };

  /**
   * Renders the action buttons if showButtons is true
   */
  const Actions = () => {
    if (!showButtons) return null;
    return (
      <div className="flex flex-col gap-4 pt-4 xs:flex-row">
        {primaryButtonLabel && (
          <Button
            disabled={!onGoToDashboard}
            label={primaryButtonLabel}
            onClick={onGoToDashboard}
            size="small"
            variant="primary"
          />
        )}
        {secondaryButtonLabel && (
          <Button
            disabled={!onRetakeRoleplay}
            label={secondaryButtonLabel}
            onClick={onRetakeRoleplay}
            size="small"
            variant="secondary"
          />
        )}
      </div>
    );
  };

  // =======================================
  // Render
  // =======================================
  return (
    <div
      className={cn('flex w-full flex-col gap-4 rounded-lg bg-neutral-white p-6', className)}
      {...props}
    >
      <Title title={title} />
      <Progress progress={clampedProgress} />
      <Spinner size={48} />
      <Description description={description} />
      <Actions />
    </div>
  );
};

ScoreProcessingCard.displayName = 'ScoreProcessingCard';

export default ScoreProcessingCard;
